import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface RouteMeta {
  title: string
  parent?: string
}

const ROUTE_META: Record<string, RouteMeta> = {
  '/dashboard': { title: '送ったメールの成績' },
  '/chat': { title: 'AI営業チャット' },
  '/flows': { title: 'フロー' },
  '/leads': { title: '営業リスト' },
  '/mail': { title: 'メール' },
  '/send': { title: 'メール作成' },
  '/sequences': { title: 'シーケンス' },
  '/reply-inbox': { title: '返信' },
  '/settings': { title: '設定' },
  '/smart-email': { title: 'メール管理' },
  '/email-management': { title: 'メール管理' },
  '/ai-lead-discovery': { title: 'AIで見込み客発見', parent: '/leads' },
  '/settings/email-limits': { title: '送信上限の管理', parent: '/settings' },
  '/settings/email-service': { title: 'メールサービス設定', parent: '/settings' },
  '/admin/email-limits': { title: '送信上限の管理', parent: '/settings' },
  '/admin/email-service-settings': { title: 'メールサービス設定', parent: '/settings' },
  '/company-profile': { title: '企業プロファイル' },
  '/email-lists': { title: 'メールリスト', parent: '/leads' },
}

interface Crumb {
  label: string
  href?: string
}

const isFlowDetail = (path: string) => /^\/flows\/[^/]+$/.test(path)
const isDynamicListDetail = (path: string) => /^\/email-lists\/[^/]+$/.test(path)
const isDynamicListSend = (path: string) => /^\/email-lists\/[^/]+\/send$/.test(path)

const buildCrumbs = (pathname: string): Crumb[] => {
  if (isDynamicListSend(pathname)) {
    const listId = pathname.split('/')[2]
    return [
      { label: '営業リスト', href: '/leads' },
      { label: 'リスト詳細', href: `/email-lists/${listId}` },
      { label: 'フォローアップ送信' },
    ]
  }
  if (isDynamicListDetail(pathname)) {
    return [
      { label: '営業リスト', href: '/leads' },
      { label: 'リスト詳細' },
    ]
  }
  // The flow's own name is on the page itself (FlowDetailPage), which is the
  // one place that knows it; the crumb only has to lead back to the list.
  if (isFlowDetail(pathname)) {
    return [{ label: 'フロー', href: '/flows' }, { label: 'フローの中身' }]
  }
  const meta = ROUTE_META[pathname]
  if (!meta) return []
  const trail: Crumb[] = []
  if (meta.parent) {
    const parentMeta = ROUTE_META[meta.parent]
    if (parentMeta) trail.push({ label: parentMeta.title, href: meta.parent })
  }
  trail.push({ label: meta.title })
  return trail
}

const buildTitle = (pathname: string): string => {
  if (isDynamicListSend(pathname)) return 'フォローアップ送信'
  if (isDynamicListDetail(pathname)) return 'リスト詳細'
  if (isFlowDetail(pathname)) return 'フローの中身'
  return ROUTE_META[pathname]?.title || ''
}

/** Routes whose heading belongs to the page and not to this bar. A flow's own
 *  name is the only title worth putting on 「フローの中身」, and FlowDetailPage
 *  is the one place that knows it — so the bar keeps the trail and the back
 *  button and leaves the <h1> alone. Two headings for one screen is what this
 *  avoids: the bar used to say 「フローの中身」 directly above the page's own
 *  「大阪・製造業ライン」. */
const PAGE_OWNS_HEADING = (pathname: string) => isFlowDetail(pathname)

const HIDDEN_ROUTES = new Set(['/', '/login', '/signup', '/oauth/gmail/launch', '/oauth/gmail/callback'])

interface PageHeaderProps {
  onMenuToggle?: () => void
  mobileOpen?: boolean
  menuButtonRef?: React.Ref<HTMLButtonElement>
}

const PageHeader: React.FC<PageHeaderProps> = ({ onMenuToggle, mobileOpen, menuButtonRef }) => {
  const location = useLocation()
  const navigate = useNavigate()

  if (HIDDEN_ROUTES.has(location.pathname)) return null

  const title = buildTitle(location.pathname)
  if (!title) return null

  const crumbs = buildCrumbs(location.pathname)
  const canGoBack = crumbs.length > 1

  return (
    <header
      style={{
        background: '#fff',
        borderBottom: '1px solid #e5e7eb',
        padding: '0.875rem 1.5rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
      }}
    >
      {onMenuToggle && (
        <button
          ref={menuButtonRef}
          type="button"
          onClick={onMenuToggle}
          className="app-mobile-menu-button"
          aria-label={mobileOpen ? 'メニューを閉じる' : 'メニューを開く'}
          aria-expanded={mobileOpen ?? false}
          aria-controls="app-sidebar-nav"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="3" y1="6" x2="21" y2="6"/>
            <line x1="3" y1="12" x2="21" y2="12"/>
            <line x1="3" y1="18" x2="21" y2="18"/>
          </svg>
        </button>
      )}
      <div style={{ minWidth: 0, flex: 1 }}>
        {crumbs.length > 1 && (
          <nav
            aria-label="パンくず"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              fontSize: '0.75rem',
              color: '#6b7280',
              marginBottom: '0.25rem',
            }}
          >
            {crumbs.map((c, i) => {
              const isLast = i === crumbs.length - 1
              return (
                <React.Fragment key={`${c.label}-${i}`}>
                  {c.href && !isLast ? (
                    <button
                      type="button"
                      onClick={() => navigate(c.href!)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        padding: 0,
                        color: '#6b7280',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                      }}
                    >
                      {c.label}
                    </button>
                  ) : (
                    <span style={{ color: isLast ? '#374151' : '#6b7280' }}>{c.label}</span>
                  )}
                  {!isLast && <span aria-hidden="true">/</span>}
                </React.Fragment>
              )
            })}
          </nav>
        )}
        {!PAGE_OWNS_HEADING(location.pathname) && (
          <h1
            style={{
              margin: 0,
              fontSize: '1.125rem',
              fontWeight: 600,
              color: '#111827',
              letterSpacing: '-0.01em',
            }}
          >
            {title}
          </h1>
        )}
      </div>
      {canGoBack && (
        <button
          type="button"
          onClick={() => navigate(-1)}
          style={{
            background: '#fff',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            padding: '0.375rem 0.75rem',
            fontSize: '0.8125rem',
            fontWeight: 500,
            color: '#374151',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.25rem',
          }}
        >
          <span aria-hidden="true">←</span>戻る
        </button>
      )}
    </header>
  )
}

export default PageHeader
